/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { translate } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';

//----------------------------------------------------------------------//
// GLOBAL COMPONENTS                                                    //
//----------------------------------------------------------------------//

import Loading from 'components/Loading/Loading';

//----------------------------------------------------------------------//
// CSS                                                                  //
//----------------------------------------------------------------------//

import styles from './ListExpandable.module.scss';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

type TypeListExpandable = {
  title: string,
  render: ?Function,
  onExpand: Function,
  children: ?React.Element<*>,
};

/**
 * ListExpandable is made to render a list of items in two ways:
 * 1) By children (just pass children in the classic React way) and these will be rendered when component expands.
 * 2) By loading items from API. In this case you can pass a
 *      a) string "title" - the title of expandable list
 *      b) function "loading" - with loader component
 *      c) function "onExpand" - that will be triggered when component expands and will perform API call.
 *         When you perform API call you have to do async function : onExpand={async() => await dosomething()}
 *      d) function "render" - that will be called in main body of ListExpandable with "data" that comes from API call.
 *                             This is where you can render a list of data from the API. That attribute
 *                             could have been called listRender.
 *
 * You can always pass children into the body of ListExpandable component and these children will be always rendered.
 * That could be an if statement of course but that a work to be done later :)
 *
 * Example: Please refer to "Countries" Component for now. README.MD is coming soon... :)
 */
@translate()
@inject('assetStore')
@observer
export default class ListExpandable extends React.Component<TypeListExpandable> {
  static defaultProps = {
    title: '',
    render: null,
    onExpand: false,
    loading: null,
    children: null,
  };

  @observable panelOpen: boolean = false;

  @observable loading: boolean = false;

  @observable data: any = null;

  /**
   * This function will perform a callback that is expected to provide data that will trigger re-rendering
   * of component and that data will be passed into "props.render" function for custom rendering in parent component.
   *
   * @returns {Promise<void>}
   */
  onExpand = async () => {
    this.panelOpen = !this.panelOpen;

    if (this.panelOpen && typeof this.props.onExpand === 'function') {
      this.loading = true;
      this.data = await this.props.onExpand();
      this.loading = false;
    }
  };

  render(): React.Element<*> {
    const title: string = this.props.title;

    return (
      <List component="ul" className={styles.expandedList} disablePadding={true}>
        <ListItem
          button={false}
          className={`${styles.expandedListItemHeader} ${styles.uppercase}`}
          onClick={this.onExpand}
        >
          <ListItemText inset={false} primary={title} />
          {this.loading && (
            <span className={styles.loadingIcon}>
              <Loading variant="small" />
            </span>
          )}
          <ListItemIcon className={styles.collapseIcon}>
            <svg>
              <use
                xlinkHref={
                  this.panelOpen && !this.loading ? '#icon-chevron-down' : '#icon-chevron-up'
                }
              />
            </svg>
          </ListItemIcon>
        </ListItem>

        <Collapse
          in={this.panelOpen && !this.loading}
          timeout="auto"
          unmountOnExit={true}
          className={styles.expandedCollapse}
        >
          {this.props.children && this.props.children}
          {this.props.render && this.props.render(this.data)}
        </Collapse>
      </List>
    );
  }
}
